import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import SeoHead from "@/components/SeoHead";
import Ornament from "@/components/Ornament";

const COPY = {
  fr: {
    kicker: "Hospitalité normande",
    title: "Dormir en baie du Mont-Saint-Michel",
    lead: "À la recherche d'un hôtel ou d'un hébergement face au Mont-Saint-Michel ? Le Prieuré saint Léonard de Vains vous ouvre ses portes : un monument roman millénaire (1027) posé sur les grèves, à 30 minutes à pied de la baie et 15 minutes d'Avranches. Une alternative authentique à l'hôtel — dans l'esprit des plus beaux séjours de charme (Airbnb premium, Booking).",
    offersTitle: "Deux façons de séjourner",
    offers: [
      {
        to: "/sejour",
        img: "/photos/prieure-accueil.jpg",
        title: "Le Logis prieural",
        body: "Chambres de caractère dans les murs du Prieuré : retraites spirituelles, journées d'études, week-ends en famille. Le charme d'une chambre d'hôtes, la profondeur d'un lieu millénaire.",
        cta: "Découvrir le Logis",
      },
      {
        to: "/bergerie",
        img: "/photos/prieure-mont-bay.jpg",
        title: "La Bergerie pèlerine",
        body: "Hébergement insolite au bord des grèves, point de départ des traversées de la baie vers le Mont-Saint-Michel. Un gîte de charme entre herbus, moutons de prés-salés et grand large.",
        cta: "Découvrir la Bergerie",
      },
    ],
    whyTitle: "Pourquoi dormir au Prieuré ?",
    why: [
      { t: "La baie pour horizon", b: "Vue sur la baie du Mont-Saint-Michel, couchers de soleil sur les grèves, silence d'un promontoire classé parmi les plus belles baies du monde." },
      { t: "Départ des traversées", b: "Les traversées guidées des grèves vers le Mont partent du domaine — dans les pas des pèlerins des chemins de Saint-Michel." },
      { t: "Au cœur de la Normandie historique", b: "À 15 min d'Avranches et de son Scriptorial, 40 min du Mont-Saint-Michel par la route, sur la route historique des Abbayes de Normandie, de Caen au Mont." },
    ],
    faqTitle: "Questions fréquentes",
    faq: [
      { q: "Le Prieuré saint Léonard est-il un hôtel ?", a: "Non — c'est mieux : un monument roman millénaire qui pratique l'hospitalité. Les chambres du Logis prieural et la Bergerie pèlerine offrent le confort d'un hébergement de charme, avec l'âme d'un lieu de patrimoine." },
      { q: "À quelle distance est le Mont-Saint-Michel ?", a: "Le Mont est visible depuis les grèves du domaine. Comptez environ 40 minutes par la route, ou une traversée guidée de la baie à pied (3 à 4 heures) au départ de la Bergerie des Grèves." },
      { q: "Peut-on réserver via Booking ou Airbnb ?", a: "Les demandes se font directement auprès du Prieuré via la page Contact ou la page Séjour — réponse rapide et accueil personnalisé, dans l'esprit des séjours premium." },
      { q: "L'hébergement est-il ouvert pendant les Fêtes 2027 ?", a: "Oui — les villages d'accueil de la baie et le domaine du Prieuré hébergeront les délégations et les pèlerins du 1ᵉʳ au 5 juillet 2027. Réservez tôt : le Millénaire attirera de nombreux visiteurs." },
    ],
    ctaTitle: "Préparez votre séjour",
    ctaBody: "Écrivez-nous : nous composons avec vous un séjour à la mesure du lieu — nuitées, traversée de la baie, concerts et tables normandes.",
    ctaBtn: "Nous contacter",
    ctaAgenda: "Voir l'agenda des événements",
  },
  en: {
    kicker: "Norman hospitality",
    title: "Sleeping on the Bay of Mont-Saint-Michel",
    lead: "Looking for a hotel or accommodation facing Mont-Saint-Michel? The Priory of Saint Léonard in Vains opens its doors: a thousand-year-old Romanesque landmark (1027) set on the strands, 30 minutes' walk from the bay and 15 minutes from Avranches. An authentic alternative to the hotel — in the spirit of the finest premium stays (Airbnb, Booking).",
    offersTitle: "Two ways to stay",
    offers: [
      {
        to: "/sejour",
        img: "/photos/prieure-accueil.jpg",
        title: "The Priory Lodge",
        body: "Characterful rooms within the Priory walls: spiritual retreats, study days, family weekends. The charm of a guest house, the depth of a thousand-year-old place.",
        cta: "Discover the Lodge",
      },
      {
        to: "/bergerie",
        img: "/photos/prieure-mont-bay.jpg",
        title: "The Pilgrim Sheepfold",
        body: "Unusual accommodation by the strands, starting point of the bay crossings to Mont-Saint-Michel. A charming refuge between salt meadows, sheep and open sea.",
        cta: "Discover the Sheepfold",
      },
    ],
    whyTitle: "Why sleep at the Priory?",
    why: [
      { t: "The bay as your horizon", b: "Views over the Bay of Mont-Saint-Michel, sunsets on the strands, the silence of a promontory ranked among the most beautiful bays in the world." },
      { t: "Where the crossings begin", b: "Guided crossings of the strands to the Mont leave from the estate — in the footsteps of the pilgrims of the Saint-Michel paths." },
      { t: "In the heart of historic Normandy", b: "15 min from Avranches and its Scriptorial, 40 min from Mont-Saint-Michel by road, on the historic route of the Abbeys of Normandy, from Caen to the Mont." },
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      { q: "Is the Priory of Saint Léonard a hotel?", a: "No — it is better: a thousand-year-old Romanesque landmark that practises hospitality. The Lodge rooms and the Pilgrim Sheepfold offer the comfort of charming accommodation with the soul of a heritage site." },
      { q: "How far is Mont-Saint-Michel?", a: "The Mont is visible from the estate's strands. Allow about 40 minutes by road, or a guided walk across the bay (3 to 4 hours) from the Bergerie des Grèves." },
      { q: "Can I book via Booking or Airbnb?", a: "Requests are made directly to the Priory via the Contact or Stay pages — quick replies and personal welcome, in the spirit of premium stays." },
      { q: "Is accommodation open during the 2027 Feasts?", a: "Yes — the host villages of the bay and the Priory estate will welcome delegations and pilgrims from 1 to 5 July 2027. Book early: the Millennium will draw many visitors." },
    ],
    ctaTitle: "Plan your stay",
    ctaBody: "Write to us: we will compose a stay worthy of the place — nights, bay crossing, concerts and Norman tables.",
    ctaBtn: "Contact us",
    ctaAgenda: "See the events calendar",
  },
};

const SEO_TITLES = {
  fr: "Dormir en baie du Mont-Saint-Michel — Hébergement au Prieuré",
  en: "Sleeping on the Bay of Mont-Saint-Michel — Stay at the Priory",
};
const SEO_DESC = {
  fr: "Hôtel ou hébergement en baie du Mont-Saint-Michel ? Dormez au Prieuré saint Léonard de Vains : monument roman millénaire, chambres de charme, bergerie face aux grèves, départ des traversées vers le Mont. À 15 min d'Avranches, en Normandie.",
  en: "Hotel or accommodation on the Bay of Mont-Saint-Michel? Sleep at the Priory of Saint Léonard in Vains: a thousand-year-old Romanesque landmark, charming rooms, a sheepfold facing the strands, starting point of the crossings to the Mont. 15 min from Avranches, Normandy.",
};

function FaqJsonLd({ faq }) {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq-jsonld", "1");
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [faq]);
  return null;
}

export default function Dormir() {
  const { locale } = useI18n();
  const c = COPY[locale === "fr" ? "fr" : "en"];

  return (
    <div data-testid="dormir-page" style={{ background: "var(--parchment)" }}>
      <SeoHead
        titles={SEO_TITLES}
        descriptions={SEO_DESC}
        image="/photos/prieure-sunset.jpg"
        keywords="hôtel baie du Mont-Saint-Michel, hébergement Mont-Saint-Michel, dormir baie du Mont-Saint-Michel, hôtel Avranches, hébergement de charme Normandie, gîte de charme, chambre d'hôtes, Airbnb premium, Booking, séjour Normandie, monument historique, hébergement insolite, traversée de la baie, Vains"
      />
      <FaqJsonLd faq={c.faq} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b" style={{ borderColor: "var(--gold)" }}>
        <img src="/photos/prieure-sunset.jpg" alt={c.title}
             className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,16,12,0.55), rgba(20,16,12,0.72))" }} />
        <div className="relative container-prieure pt-40 pb-24 sm:pt-48 sm:pb-32 text-center">
          <div className="font-caps text-[0.68rem] mb-4" style={{ color: "var(--gold)", letterSpacing: "0.26em" }}>
            ◇ {c.kicker.toUpperCase()} ◇
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "#fff" }}>
            {c.title}
          </h1>
          <Ornament className="justify-center mt-6" />
        </div>
      </section>

      {/* LEAD */}
      <section className="container-prieure py-14">
        <p className="max-w-3xl text-base sm:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }} data-testid="dormir-lead">
          {c.lead}
        </p>
      </section>

      {/* OFFERS */}
      <section className="container-prieure pb-16">
        <h2 className="font-display text-3xl sm:text-4xl mb-8" style={{ color: "var(--ink)" }}>{c.offersTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {c.offers.map((o) => (
            <Link key={o.to} to={o.to} data-testid={`dormir-offer-${o.to.replace("/", "")}`}
                  className="group bg-white border overflow-hidden transition-shadow hover:shadow-lg"
                  style={{ borderColor: "var(--border)" }}>
              <div className="h-56 overflow-hidden">
                <img src={o.img} alt={o.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl" style={{ color: "var(--cinnabar)" }}>{o.title}</h3>
                <p className="font-body text-sm mt-2 leading-relaxed" style={{ color: "var(--ink-2)" }}>{o.body}</p>
                <span className="inline-block mt-4 font-caps text-[0.62rem]" style={{ color: "var(--gold)", letterSpacing: "0.2em" }}>
                  {o.cta.toUpperCase()} →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="border-y" style={{ background: "var(--ink)", borderColor: "var(--gold)" }}>
        <div className="container-prieure py-16">
          <h2 className="font-display text-3xl sm:text-4xl mb-10" style={{ color: "var(--gold)" }}>{c.whyTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {c.why.map((w, i) => (
              <div key={w.t}>
                <div className="font-display text-4xl mb-3" style={{ color: "rgba(212,175,55,0.45)" }}>{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-xl mb-2" style={{ color: "#f5f0e4" }}>{w.t}</h3>
                <p className="font-body text-sm leading-relaxed" style={{ color: "rgba(245,240,228,0.75)" }}>{w.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-prieure py-16" data-testid="dormir-faq">
        <h2 className="font-display text-3xl sm:text-4xl mb-8" style={{ color: "var(--ink)" }}>{c.faqTitle}</h2>
        <div className="max-w-3xl space-y-6">
          {c.faq.map((f) => (
            <details key={f.q} className="bg-white border p-5 group" style={{ borderColor: "var(--border)" }}>
              <summary className="font-display text-lg cursor-pointer list-none flex justify-between items-center" style={{ color: "var(--ink)" }}>
                {f.q}
                <span className="ml-4 transition-transform group-open:rotate-45" style={{ color: "var(--cinnabar)" }}>+</span>
              </summary>
              <p className="font-body text-sm mt-3 leading-relaxed" style={{ color: "var(--ink-2)" }}>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-prieure pb-24 text-center">
        <Ornament className="justify-center mb-6" />
        <h2 className="font-display text-3xl" style={{ color: "var(--ink)" }}>{c.ctaTitle}</h2>
        <p className="font-body max-w-xl mx-auto mt-3" style={{ color: "var(--muted)" }}>{c.ctaBody}</p>
        <div className="flex flex-wrap justify-center gap-4 mt-7">
          <Link to="/contact" data-testid="dormir-cta-contact" className="btn btn-primary">{c.ctaBtn}</Link>
          <Link to="/agenda" data-testid="dormir-cta-agenda" className="btn btn-outline">{c.ctaAgenda}</Link>
        </div>
      </section>
    </div>
  );
}
